import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Bike, TrendingUp, Clock, IndianRupee, CheckCircle, Loader2, Phone, Mail, MapPin, Search, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { sendOtp as msg91SendOtp, verifyOtp as msg91VerifyOtp, resendOtp as msg91ResendOtp, clearOtpSession } from '../lib/msg91';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All Indian cities list for search
const ALL_INDIAN_CITIES = [
  "Agra", "Ahmedabad", "Ajmer", "Aligarh", "Allahabad", "Amravati", "Amritsar", "Aurangabad",
  "Bangalore", "Bareilly", "Belgaum", "Bhilai", "Bhiwandi", "Bhopal", "Bhubaneswar", "Bikaner",
  "Chandigarh", "Chennai", "Coimbatore", "Cuttack", "Dehradun", "Delhi", "Dhanbad", "Durgapur",
  "Faridabad", "Firozabad", "Ghaziabad", "Gorakhpur", "Gulbarga", "Guntur", "Gurgaon", "Guwahati",
  "Gwalior", "Howrah", "Hubli-Dharwad", "Hyderabad", "Indore", "Jabalpur", "Jaipur", "Jalandhar",
  "Jammu", "Jamnagar", "Jamshedpur", "Jhansi", "Jodhpur", "Jajpur", "Kakinada", "Kalyan-Dombivali", "Kanpur",
  "Kochi", "Kolhapur", "Kolkata", "Kota", "Kozhikode", "Kurnool", "Lucknow", "Ludhiana",
  "Madurai", "Maheshtala", "Malegaon", "Mangalore", "Meerut", "Moradabad", "Mumbai", "Mysore",
  "Nagpur", "Nanded", "Nashik", "Navi Mumbai", "Nellore", "New Delhi", "Noida", "Patna",
  "Pimpri-Chinchwad", "Pondicherry", "Pune", "Raipur", "Rajahmundry", "Rajkot", "Ranchi", "Rourkela",
  "Saharanpur", "Salem", "Sangli", "Shimla", "Siliguri", "Solapur", "Srinagar", "Surat",
  "Thane", "Thiruvananthapuram", "Thrissur", "Tiruchirappalli", "Tiruppur", "Udaipur", "Ujjain",
  "Ulhasnagar", "Vadodara", "Varanasi", "Vasai-Virar", "Vijayawada", "Visakhapatnam", "Warangal"
];

export const DeliveryPartner = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('partner');
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [filteredCities, setFilteredCities] = useState([]);
  
  // Modal states
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  const [otp, setOtp] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    pincode: '',
    partnership_type: 'partner',
  });
  const [otpMode, setOtpMode] = useState('demo'); // 'demo', 'twilio', or 'msg91'

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (citySearch.length > 0) {
      const filtered = ALL_INDIAN_CITIES.filter(city => 
        city.toLowerCase().includes(citySearch.toLowerCase())
      );
      setFilteredCities(filtered);
      setShowCityDropdown(true);
    } else {
      setFilteredCities([]);
      setShowCityDropdown(false);
    }
  }, [citySearch]);

  const handleChange = (e) => {
    const value = e.target.value;
    const name = e.target.name;
    
    if (name === 'phone' || name === 'pincode') {
      if (!/^\d*$/.test(value)) return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleCitySelect = (city) => {
    setFormData({ ...formData, city });
    setCitySearch(city);
    setShowCityDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 10) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }
    
    if (formData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }
    
    if (!formData.city) {
      toast.error('Please select your city');
      return;
    }
    
    // Show Agreement Modal first
    setShowAgreementModal(true);
  };

  const handleAgreementAccept = () => {
    if (!agreementAccepted) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    setShowAgreementModal(false);
    setAgreementAccepted(false); // Reset for consent modal
    setShowConsentModal(true);
  };

  const handleConsentAccept = async () => {
    if (!agreementAccepted) {
      toast.error('Please accept the consent agreement');
      return;
    }
    setShowConsentModal(false);
    setLoading(true);

    try {
      const response = await axios.post(`${API}/partners/delivery`, {
        ...formData,
        partnership_type: activeTab,
      });
      
      setPartnerId(response.data.id);
      
      // Check with backend what OTP mode to use
      const otpResponse = await axios.post(`${API}/auth/send-otp`, {
        phone: formData.phone,
      });
      
      const mode = otpResponse.data.mode || 'demo';
      setOtpMode(mode);
      
      // For MSG91 mode, use the frontend SDK to send OTP
      if (mode === 'msg91' && otpResponse.data.use_sdk) {
        try {
          await msg91SendOtp(formData.phone);
          toast.success('Application submitted! Please verify your phone number.');
        } catch (sdkError) {
          console.error('MSG91 SDK error:', sdkError);
          toast.error('Failed to send OTP. Please try again.');
          setLoading(false);
          return;
        }
      } else {
        toast.success('Application submitted! Please verify your phone number.');
        
        // Show demo OTP for testing
        if (otpResponse.data.demo_otp) {
          toast.info(`Demo OTP: ${otpResponse.data.demo_otp}`, {
            duration: 30000,
            description: 'Use this OTP for testing'
          });
        }
      }
      
      setShowOtpModal(true);
      setCountdown(60);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    setOtpLoading(true);
    
    try {
      // For MSG91 mode, verify via SDK first, then backend
      if (otpMode === 'msg91') {
        try {
          // Verify OTP with MSG91 SDK and get access token
          const { accessToken } = await msg91VerifyOtp(otp);
          
          // Verify access token with backend
          const verifyRes = await axios.post(`${API}/auth/msg91/verify`, {
            access_token: accessToken,
            role: 'delivery_partner',
            phone: formData.phone
          });
          
          if (!verifyRes.data.success) {
            throw new Error('Backend verification failed');
          }
          
          // Also update partner verification status
          await axios.post(`${API}/partners/delivery/verify`, {
            partner_id: partnerId,
            phone: formData.phone,
            otp: otp,
            msg91_verified: true
          }).catch(() => {});
          
          toast.success('Phone verified successfully! Redirecting to KYC...');
          setShowOtpModal(false);
          clearOtpSession();
          
          // Store partner info for KYC page
          localStorage.setItem('fudco_delivery_partner', JSON.stringify({
            id: partnerId,
            name: formData.name,
            phone: formData.phone
          }));
          
          // Redirect to KYC page
          navigate('/delivery-partner/kyc');
          return;
        } catch (sdkError) {
          console.error('MSG91 verification error:', sdkError);
          toast.error('Invalid or expired OTP. Please try again.');
          setOtpLoading(false);
          return;
        }
      }
      
      // For demo/twilio mode, verify via backend
      await axios.post(`${API}/partners/delivery/verify`, {
        partner_id: partnerId,
        phone: formData.phone,
        otp: otp,
      });
      
      toast.success('Phone verified successfully! Redirecting to KYC...');
      setShowOtpModal(false);
      
      // Store partner info for KYC page
      localStorage.setItem('fudco_delivery_partner', JSON.stringify({
        id: partnerId,
        name: formData.name,
        phone: formData.phone
      }));
      
      // Redirect to KYC page
      navigate('/delivery-partner/kyc');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (countdown > 0) return;
    
    try {
      if (otpMode === 'msg91') {
        // Use MSG91 SDK to resend
        await msg91ResendOtp(formData.phone, 'sms');
        toast.success('OTP resent successfully!');
      } else {
        // Use backend to resend
        const otpResponse = await axios.post(`${API}/auth/send-otp`, {
          phone: formData.phone,
        });
        toast.success('OTP resent successfully!');
        
        if (otpResponse.data.demo_otp) {
          toast.info(`Demo OTP: ${otpResponse.data.demo_otp}`, {
            duration: 30000,
            description: 'Use this OTP for testing'
          });
        }
      }
      setCountdown(60);
    } catch (error) {
      toast.error('Failed to resend OTP. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white">
      {/* Hero Section */}
      <section className="fudco-section bg-[#0A2F0A] text-white py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-32 h-32 rounded-full bg-[#FFD700]"></div>
          <div className="absolute bottom-10 left-20 w-48 h-48 rounded-full bg-[#2D8B2E]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex-1 text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Deliver with <span className="text-[#FFD700]">fuDco</span>
              </h1>
              <p className="text-xl text-gray-300 mb-6">
                Be your own boss. Earn on your schedule.
              </p>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <IndianRupee className="w-5 h-5 text-[#FFD700]" />
                  <span>₹25,000+ Monthly</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                  <Clock className="w-5 h-5 text-[#FFD700]" />
                  <span>Flexible Hours</span>
                </div>
              </div>
            </div>
            
            <div className="flex-1 grid grid-cols-2 gap-4 max-w-md">
              <Card className="bg-white/10 backdrop-blur p-4 text-center border-0">
                <TrendingUp className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                <p className="text-2xl font-bold">₹50+</p>
                <p className="text-sm text-gray-300">Per Delivery</p>
              </Card>
              <Card className="bg-white/10 backdrop-blur p-4 text-center border-0">
                <Bike className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                <p className="text-2xl font-bold">5000+</p>
                <p className="text-sm text-gray-300">Active Partners</p>
              </Card>
              <Card className="bg-white/10 backdrop-blur p-4 text-center border-0 col-span-2">
                <CheckCircle className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                <p className="text-lg font-bold">₹200 Instant Bonus</p>
                <p className="text-sm text-gray-300">On completing first delivery</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="fudco-section py-12 px-4">
        <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
          <Card className="p-8 shadow-xl border-0">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-[#0A2F0A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Delivery Partner Registration
              </h2>
              <p className="text-gray-600">Start earning with Fudco today</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8">
                <TabsTrigger value="partner" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  Partner With Us
                </TabsTrigger>
                <TabsTrigger value="consulting" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
                  Delivery Consultant
                </TabsTrigger>
              </TabsList>

              <TabsContent value="partner" className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="flex">
                      <div className="flex items-center px-4 bg-gray-100 border border-r-0 border-gray-200 rounded-l-xl text-gray-600">
                        +91
                      </div>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="10-digit number"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        required
                        className="h-12 rounded-l-none rounded-r-xl"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2 relative">
                    <Label>City *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search city..."
                        value={citySearch}
                        onChange={(e) => {
                          setCitySearch(e.target.value);
                          setFormData({ ...formData, city: '' });
                        }}
                        className="h-12 rounded-xl pl-10"
                      />
                    </div>
                    {showCityDropdown && filteredCities.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {filteredCities.slice(0, 10).map((city) => (
                          <button
                            key={city}
                            type="button"
                            onClick={() => handleCitySelect(city)}
                            className="w-full px-4 py-3 text-left hover:bg-yellow-50 flex items-center gap-2"
                          >
                            <MapPin className="w-4 h-4 text-[#2D8B2E]" />
                            {city}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      type="text"
                      placeholder="6-digit pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      maxLength={6}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="consulting" className="space-y-6">
                <div className="text-center py-8">
                  <Bike className="w-16 h-16 text-[#FFD700] mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">Delivery Consulting Services</h3>
                  <p className="text-gray-600 mb-6">
                    Help us expand our delivery network in your area
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12 rounded-xl"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter email address"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <div className="flex">
                      <div className="flex items-center px-4 bg-gray-100 border border-r-0 border-gray-200 rounded-l-xl text-gray-600">
                        +91
                      </div>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="10-digit number"
                        value={formData.phone}
                        onChange={handleChange}
                        maxLength={10}
                        required
                        className="h-12 rounded-l-none rounded-r-xl"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2 relative">
                    <Label>City *</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search city..."
                        value={citySearch}
                        onChange={(e) => {
                          setCitySearch(e.target.value);
                          setFormData({ ...formData, city: '' });
                        }}
                        className="h-12 rounded-xl pl-10"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode">Pincode *</Label>
                    <Input
                      id="pincode"
                      name="pincode"
                      type="text"
                      placeholder="6-digit pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      maxLength={6}
                      required
                      className="h-12 rounded-xl"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 mt-8 bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full font-semibold text-lg"
            >
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <>
                  Submit Application
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </Card>

          {/* Contact Info */}
          <section className="mt-8 text-center">
            <p className="text-gray-600 mb-2">Need help with registration?</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:service@fudco.in" className="flex items-center gap-2 text-[#2D8B2E] hover:underline">
                <Mail className="w-4 h-4" />
                <span>service@fudco.in</span>
              </a>
            </div>
          </section>
        </form>
      </section>

      {/* Full Delivery Partner Agreement Modal */}
      <Dialog open={showAgreementModal} onOpenChange={setShowAgreementModal}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="delivery-agreement-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center text-[#1b5e20] border-b-2 border-[#1b5e20] pb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Delivery Partner Terms and Conditions
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 text-sm leading-relaxed">
            {/* Notice Box */}
            <div className="bg-gray-100 p-4 rounded-lg border-l-4 border-[#1b5e20]">
              <p className="text-gray-700">
                This document is an electronic record in terms of the Information Technology Act, 2000 and rules made thereunder.
                This electronic record does not require physical or digital signatures.
              </p>
              <p className="text-gray-700 mt-2">
                By clicking on the <strong>"I ACCEPT"</strong> button, you agree to be bound by these Delivery Partner Terms and Conditions
                ("Delivery Partner T&C") for using the <strong>Fudco Delivery Partner Platform</strong>.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">1. Parties</h3>
              <p className="text-gray-700">
                These Delivery Partner T&C constitute a binding agreement between:
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Traderow Food Serve Private Limited</strong>, operating under the brand name
                <strong> Fudco</strong> ("Fudco", "We", "Us", "Our")
                <br /><br />AND<br /><br />
                An individual delivery partner ("Delivery Partner", "You", "Your").
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">2. Definitions</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>"Acceptance"</strong> means clicking the "I ACCEPT" button.</li>
                <li><strong>"Agreement"</strong> means the Delivery Services Availability Agreement executed between Fudco and the Delivery Partner.</li>
                <li><strong>"Applicable Law"</strong> means all laws applicable in India.</li>
                <li><strong>"Delivery Partner Platform"</strong> or <strong>"Fudco DP Platform"</strong> means the mobile application and backend systems used for delivery services.</li>
                <li><strong>"Delivery Services"</strong> means order acceptance, pickup, and delivery of food and beverages to users.</li>
                <li><strong>"Delivery Partner Information"</strong> includes KYC, vehicle details, license, insurance, and location data.</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">3. Applicability</h3>
              <p className="text-gray-700">
                These Delivery Partner T&C together with the Agreement form the complete understanding between the Parties.
                Continued use of the Fudco DP Platform constitutes acceptance of updates made from time to time.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">4. Services & Charges</h3>
              <p className="text-gray-700">
                The Delivery Partner agrees to make himself/herself available on the Fudco DP Platform to undertake Delivery Services.
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 mt-2">
                <li>Delivery Services constitute a separate contract between the Delivery Partner and the User.</li>
                <li>Fudco acts only as a technology intermediary.</li>
                <li>Onboarding Fee / Security Deposit / Training Fees may be charged at Fudco's discretion.</li>
                <li>Platform Charges may be revised from time to time.</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">5. Payments & Incentives</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Delivery Charges shall be determined by Fudco using demand, distance, weather, and traffic factors.</li>
                <li>Delivery Partner authorizes Fudco to collect Delivery Charges on their behalf.</li>
                <li>Payouts shall be made weekly after deductions.</li>
                <li>Fudco may provide incentives or availability fees at its discretion.</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">6. Delivery Partner Obligations</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Maintain valid driving license, vehicle registration, and insurance</li>
                <li>Ensure food safety and non-tampering of packages</li>
                <li>Use own vehicle at own cost</li>
                <li>Follow traffic laws and safety standards</li>
                <li>Do not delegate delivery services to third parties</li>
                <li>Maintain professional behavior with Users and Restaurant Partners</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">7. Taxes</h3>
              <p className="text-gray-700">
                You authorize Fudco to deduct applicable TDS and other taxes under the Income Tax Act, 1961 and GST laws.
                Tax invoices shall be issued as required under law.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">8. Relationship of Parties</h3>
              <p className="text-gray-700">
                The Delivery Partner is an independent contractor.
                Nothing herein creates an employer–employee relationship, partnership, or agency.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">9. Intellectual Property</h3>
              <p className="text-gray-700">
                All intellectual property related to the Fudco Platform belongs exclusively to Fudco.
                No rights or licenses are granted except for limited use during Delivery Services.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">10. Termination</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Fudco may terminate access at any time without notice.</li>
                <li>Immediate termination may occur for fraud, misconduct, poor performance, or legal violations.</li>
                <li>Delivery Partner may terminate with 15 days prior written notice.</li>
              </ul>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">11. Indemnity & Limitation of Liability</h3>
              <p className="text-gray-700">
                The Delivery Partner agrees to indemnify Fudco against losses arising from misconduct, negligence,
                legal violations, or damage caused during Delivery Services.
              </p>
              <p className="text-gray-700 mt-2">
                Fudco's total liability shall not exceed INR 10,000/-.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">12. Confidentiality & Data Usage</h3>
              <p className="text-gray-700">
                Delivery Partner information shall be used as per Fudco's Privacy Policy.
                Confidential business information shall not be disclosed to third parties.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">13. Governing Law & Jurisdiction</h3>
              <p className="text-gray-700">
                These Delivery Partner T&C shall be governed by the laws of India.
                Courts of New Delhi shall have exclusive jurisdiction.
              </p>
            </div>

            <div className="mt-6">
              <h3 className="font-bold text-[#1b5e20] text-lg mb-2">14. Miscellaneous</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li>Force Majeure applies</li>
                <li>Invalid provisions shall not affect remaining clauses</li>
                <li>Entire Agreement supersedes prior understandings</li>
              </ul>
            </div>

            {/* Company Details Footer */}
            <div className="mt-8 p-5 bg-gray-100 rounded-xl border-l-4 border-[#1b5e20]">
              <p className="text-gray-700">
                <strong>Legal Entity:</strong> Traderow Food Serve Private Limited<br />
                <strong>Brand:</strong> Fudco<br />
                <strong>Website:</strong> www.fudco.in
              </p>
            </div>
            
            <div className="flex items-start gap-3 p-4 bg-yellow-50 rounded-xl mt-6">
              <Checkbox 
                id="delivery-agreement" 
                checked={agreementAccepted}
                onCheckedChange={setAgreementAccepted}
                className="mt-1"
                data-testid="delivery-agreement-checkbox"
              />
              <label htmlFor="delivery-agreement" className="text-sm text-gray-700 cursor-pointer">
                I have read and agree to the <strong>Delivery Partner Terms & Conditions</strong>.
              </label>
            </div>
            
            <Button
              onClick={handleAgreementAccept}
              disabled={!agreementAccepted}
              className="w-full h-12 bg-[#1b5e20] hover:bg-[#145216] text-white rounded-lg font-semibold"
              data-testid="delivery-agreement-accept-btn"
            >
              I Accept
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Consent Summary Modal */}
      <Dialog open={showConsentModal} onOpenChange={setShowConsentModal}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="delivery-consent-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1b5e20]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Delivery Partner Agreement – Fudco
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span>You are joining as an <strong>independent delivery partner</strong>, not an employee.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span>You will deliver orders using your <strong>own vehicle</strong> and valid documents.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span>Delivery charges and incentives are <strong>decided by Fudco</strong> and paid weekly.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span>You must <strong>not tamper with food</strong> and follow safety & traffic laws.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span>Customer and order data is <strong>strictly confidential</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-[#1b5e20] flex-shrink-0 mt-0.5" />
                <span>Fudco may suspend or deactivate access for <strong>fraud, misconduct, or policy violations</strong>.</span>
              </li>
            </ul>
            
            <div className="p-4 bg-gray-100 rounded-xl">
              <div className="flex items-start gap-3">
                <Checkbox 
                  id="delivery-consent" 
                  checked={agreementAccepted}
                  onCheckedChange={setAgreementAccepted}
                  className="mt-1"
                  data-testid="delivery-consent-checkbox"
                />
                <label htmlFor="delivery-consent" className="text-sm text-gray-700 cursor-pointer">
                  I agree to the <strong>Fudco Delivery Partner Terms & Conditions</strong> and consent to continue.
                </label>
              </div>
            </div>
            
            <Button
              onClick={handleConsentAccept}
              disabled={!agreementAccepted || loading}
              className="w-full h-12 bg-[#1b5e20] hover:bg-[#145216] text-white rounded-lg font-semibold"
              data-testid="delivery-consent-accept-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                'Continue to OTP Verification'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* OTP Verification Modal */}
      <Dialog open={showOtpModal} onOpenChange={setShowOtpModal}>
        <DialogContent className="sm:max-w-md" data-testid="delivery-otp-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Verify Phone Number
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            <div className="text-center">
              <div className="bg-yellow-100 text-yellow-700 p-4 rounded-full inline-block mb-4">
                <Phone className="w-8 h-8" />
              </div>
              <p className="text-gray-600 text-sm">
                OTP sent to <span className="font-semibold">+91 {formData.phone}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="delivery-otp">Enter OTP</Label>
              <Input
                id="delivery-otp"
                type="text"
                value={otp}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value)) {
                    setOtp(e.target.value);
                  }
                }}
                placeholder="Enter 6-digit OTP"
                maxLength={6}
                className="h-14 text-center text-2xl tracking-[0.5em] rounded-xl font-mono"
                data-testid="delivery-otp-input"
              />
            </div>

            <Button
              onClick={handleVerifyOtp}
              disabled={otpLoading || otp.length !== 6}
              className="w-full h-12 bg-[#FFD700] hover:bg-[#e6c200] text-black rounded-full font-semibold"
              data-testid="delivery-verify-otp-btn"
            >
              {otpLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Continue to KYC'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendOtp}
                disabled={countdown > 0}
                className={`text-sm ${countdown > 0 ? 'text-gray-400' : 'text-[#2D8B2E] hover:underline'}`}
                data-testid="delivery-resend-otp-btn"
              >
                {countdown > 0 ? `Resend OTP in ${countdown}s` : 'Resend OTP'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
